import React, { useEffect, useState, useContext } from "react";
import { collection, getDocs, doc, updateDoc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../services/AuthContext";
import "./AdminRoles.css";

export default function AdminRoles() {
    const { db, firebaseUid } = useContext(AuthContext);
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [permissionsList, setPermissionsList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingId, setUpdatingId] = useState(null);

    useEffect(() => {
        // Usamos directamente firebaseUid para validar
        if (firebaseUid) {
            checkAccessAndLoadData();
        } else {
            navigate("/");
        }
    }, [firebaseUid, navigate]);

    const checkAccessAndLoadData = async () => {
        setLoading(true);
        try {
            // 1. Verificación de seguridad
            const currentUserRef = doc(db, "userRoles", firebaseUid);
            const currentUserSnap = await getDoc(currentUserRef);

            if (!currentUserSnap.exists() || currentUserSnap.data().admin !== true) {
                alert("Acceso denegado: No tienes permisos de administrador.");
                navigate("/");
                return;
            }

            // 2. Cargar toda la lista de usuarios
            const snapshot = await getDocs(collection(db, "userRoles"));
            const usersData = snapshot.docs.map(doc => ({
                ...doc.data(),
                uid: doc.id // Se coloca AL FINAL para garantizar que doc.id no sea sobreescrito
            }));

            // 3. Extraer permisos dinámicamente
            const extractedPermissions = new Set();
            usersData.forEach(userData => {
                Object.entries(userData).forEach(([key, value]) => {
                    if (typeof value === "boolean") {
                        extractedPermissions.add(key);
                    }
                });
            });

            const allPermsArray = Array.from(extractedPermissions);

            /*
             * =========================================================
             * Sincronizar permisos faltantes en la base de datos
             * =========================================================
             */
            const updatePromises = [];

            const syncedUsersData = usersData.map(userData => {
                const missingPerms = {};
                let needsUpdate = false;

                allPermsArray.forEach(perm => {
                    if (!(perm in userData)) {
                        missingPerms[perm] = false;
                        needsUpdate = true;
                    }
                });

                if (needsUpdate) {
                    const userRef = doc(db, "userRoles", userData.uid);
                    updatePromises.push(updateDoc(userRef, missingPerms));

                    return { ...userData, ...missingPerms };
                }

                return userData;
            });

            if (updatePromises.length > 0) {
                console.log(`Sincronizando permisos faltantes en ${updatePromises.length} usuarios...`);
                await Promise.all(updatePromises);
            }
            /* ========================================================= */

            // 4. Formatear la cabecera de la tabla
            const permissionOrder = [
                'admin',
                'canReadCNA',
                'canEditCNA',
                'canReadPT8',
                'canEditPT8',
                'canReadUnav',
                'canEditUnav'
            ];

            const permissionLabels = {
                admin: 'Admin',
                canReadCNA: 'Can Read CNA',
                canEditCNA: 'Can Edit CNA',
                canReadPT8: 'Can Read PT8',
                canEditPT8: 'Can Edit PT8',
                canReadUnav: 'Can Read Unav',
                canEditUnav: 'Can Edit Unav'
            };

            const dynamicPerms = allPermsArray
                .map(perm => ({
                    id: perm,
                    label: permissionLabels[perm] || perm
                }))
                .sort((a, b) => {
                    return permissionOrder.indexOf(a.id) - permissionOrder.indexOf(b.id);
                });

            setPermissionsList(dynamicPerms);

            // 5. Ordenar usuarios alfabéticamente
            syncedUsersData.sort((a, b) => (a.name || "").localeCompare(b.name || ""));

            // 6. Eliminar posibles duplicados residuales antes de setear el estado
            const uniqueUsers = Array.from(new Map(syncedUsersData.map(user => [user.uid, user])).values());

            setUsers(uniqueUsers);

        } catch (error) {
            console.error("Error al cargar datos:", error);
            alert("Hubo un error al cargar la información. Redirigiendo...");
            navigate("/");
        } finally {
            setLoading(false);
        }
    };

    const togglePermission = async (targetUid, field, currentValue) => {
        setUpdatingId(targetUid);
        try {
            const userRef = doc(db, "userRoles", targetUid);
            await updateDoc(userRef, {
                [field]: !currentValue
            });

            setUsers(users.map(u =>
                u.uid === targetUid ? { ...u, [field]: !currentValue } : u
            ));
        } catch (error) {
            console.error("Error al actualizar permiso:", error);
            alert("No se pudo actualizar el permiso.");
        } finally {
            setUpdatingId(null);
        }
    };

    if (loading) {
        return <div className="admin-roles-page"><div className="text-center" style={{ padding: '50px' }}>Verificando accesos y cargando...</div></div>;
    }

    return (
        <div className="admin-roles-page">
            <div className="admin-header">
                <div>
                    <h1>Administración de Usuarios</h1>
                    <p>Gestión de accesos y permisos del sistema</p>
                </div>
                <button
                    className="btn-refresh"
                    onClick={checkAccessAndLoadData}
                    disabled={updatingId !== null}
                >
                    ↻ Refrescar Datos
                </button>
            </div>

            <div className="table-container">
                <table className="admin-table">
                    <thead>
                        <tr>
                            <th>Usuario</th>
                            {permissionsList.map(perm => (
                                <th key={perm.id} className="text-center">{perm.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={permissionsList.length + 1} className="text-center empty-state">
                                    No hay usuarios registrados.
                                </td>
                            </tr>
                        )}
                        {users.map((userData) => (
                            <tr key={userData.uid} className={updatingId === userData.uid ? "row-updating" : ""}>
                                <td>
                                    <div className="user-info">
                                        <span className="user-name">{userData.name || "Sin Nombre"}</span>
                                        <span className="user-email">{userData.email || "Sin Email"}</span>
                                    </div>
                                </td>
                                {permissionsList.map(perm => (
                                    <td key={perm.id} className="text-center">
                                        <label className="switch">
                                            <input
                                                type="checkbox"
                                                checked={!!userData[perm.id]}
                                                onChange={() => togglePermission(userData.uid, perm.id, !!userData[perm.id])}
                                                disabled={updatingId === userData.uid}
                                            />
                                            <span className="slider round"></span>
                                        </label>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}